import { Player } from './model';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import create from 'zustand';
import { useEffect } from 'react';
import { LoadingStateType, SetLoadingStateType } from '../LoadingStateType';
import { DeletePlayerFormModel } from '../../pages/settings/player/form/DeletePlayerConfirmDialog';
import { restClient } from '../../../App';
import { convertPlayerDTOs } from './playerConverter';
import { CreatePlayerRequest, PlayerLiteDTO, UpdatePlayerRequest } from '../interface/apiModels';
import { ApiException } from '../interface/ApiException';
import { ApiError } from '../interface/ApiError';

export const ALL_PLAYERS_KEY = 'players';
const usePlayers = (): UseQueryResult<Player[] | undefined, ApiException<ApiError>> => {
  // TODO verify if tenant has access
  return useQuery<Player[], ApiException<ApiError>, Player[]>(ALL_PLAYERS_KEY, fetchPlayers, {});
};

const fetchPlayers = async (): Promise<Player[]> => {
  const response = await restClient.getAllPlayers();
  return convertPlayerDTOs(response);
};

export const useCreatePlayer = (): UseMutationResult<unknown, ApiException<ApiError>, CreatePlayerRequest> => {
  const queryClient = useQueryClient();
  return useMutation(createPlayer, {
    onSuccess: () => {
      return queryClient.invalidateQueries(ALL_PLAYERS_KEY);
    },
  });
};
const createPlayer = async (request: CreatePlayerRequest): Promise<PlayerLiteDTO> => {
  return await restClient.createPlayer(request);
};

export const useUpdatePlayer = (): UseMutationResult<unknown, ApiException<ApiError>, UpdatePlayerRequest> => {
  const queryClient = useQueryClient();
  return useMutation(updatePlayer, {
    onSuccess: () => {
      return queryClient.invalidateQueries(ALL_PLAYERS_KEY);
    },
  });
};
const updatePlayer = (request: UpdatePlayerRequest) => {
  return restClient.updatePlayer(request);
  // return pyramidCupApi.players().update(request);
};

export const useDeletePlayer = (): UseMutationResult<void, ApiException<ApiError>, DeletePlayerFormModel> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePlayer,
    mutationKey: ALL_PLAYERS_KEY,
    onSuccess: () => queryClient.invalidateQueries(ALL_PLAYERS_KEY),
  });
};

const deletePlayer = async (model: DeletePlayerFormModel) => {
  await restClient.deletePlayer(model.playerId as string);
};

type PlayersStateStoreType = LoadingStateType &
  SetLoadingStateType & {
    players: Player[];
    setPlayers: (newPlayer: Player[]) => void;
    cleanup: () => void;
  };

export const playersState = create<PlayersStateStoreType>((set) => ({
  players: [],
  setPlayers: (newPlayers: Player[]) => set(() => ({ players: newPlayers })),
  cleanup: () => set({ players: [] }),
  isLoading: false,
  isError: false,
  isSuccess: false,
  error: undefined,
  setLoadingState: (isLoading, isError, isSuccess, error) =>
    set(() => ({
      isLoading: isLoading,
      isError: isError,
      isSuccess: isSuccess,
      error: error,
    })),
}));

type PlayersStateType = LoadingStateType & {
  players: Player[];
};

export const usePlayersState = (): PlayersStateType => {
  const { players, setPlayers, setLoadingState, cleanup } = playersState();
  const { data: loadedPlayers, isLoading, isError, isSuccess, error } = usePlayers();

  useEffect(() => {
    setPlayers(loadedPlayers);
    setLoadingState(isLoading, isError, isSuccess, error);
    return () => {
      cleanup();
    };
  }, [cleanup, setPlayers, loadedPlayers, isSuccess, isLoading, isError, error, setLoadingState]);
  return { players, isLoading, isError, isSuccess, error };
};
